// CategoryItem.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';

import { TranslationNamespace } from '../resource/translationNamespace.js';

import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

const CategoryItem = ({
  t,
  i18n,
  height,
  fontSize,
  text = '',
  categoryId = '',
  selected = false,
  isSelectable = false,
  isInOverview = false,
  isLink = false,
  onStatusChange = () => {},
}) => {
  let displayText = text || '';
  if (!displayText && categoryId) {
    const i18nKey = `category_${categoryId}`;
    const isTranslationExisted = i18n.customExists(i18nKey, {
      ns: TranslationNamespace.POST,
    });
    displayText = isTranslationExisted
      ? t(i18nKey, { ns: TranslationNamespace.POST })
      : categoryId;
  }

  // For the viewers, use `LinkContainer`;
  // for the creators, use `SelectableContainer` or plain `Container`
  // (depending on it should be a toggle or a simple text)
  return isLink ? (
    <LinkContainer
      to={`/category/${categoryId}`}
      $height={height}
      $isInOverview={isInOverview}
      data-element_id={'category-option'}
      data-tracking_payload={{
        'category.content': categoryId,
      }}
    >
      <LinkText $isInOverview={isInOverview}>{displayText}</LinkText>
    </LinkContainer>
  ) : isSelectable ? (
    <SelectableContainer
      $selected={selected}
      $height={height}
      onClick={() => onStatusChange({ selected: !selected, categoryId })}
    >
      <SelectableText $fontSize={fontSize}>{displayText}</SelectableText>
    </SelectableContainer>
  ) : (
    <Container $height={height}>
      <Text $fontSize={fontSize}>{displayText}</Text>
    </Container>
  );
};

CategoryItem.propTypes = {
  height: PropTypes.number,
  fontSize: PropTypes.number,
  t: PropTypes.func,
  i18n: PropTypes.object,
  text: PropTypes.string,
  categoryId: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  isSelectable: PropTypes.bool,
  isInOverview: PropTypes.bool,
  isLink: PropTypes.bool,
  onStatusChange: PropTypes.func,
};

const baseContainerStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 40px;
`;

const baseTextStyle = css`
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const Container = styled.div`
  ${baseContainerStyle}

  background-color: var(--background-color-quaternary, rgba(71, 71, 71, 0.4));
  color: var(--font-color-primary-on-dark, #fff);

  height: ${({ $height }) => $height || 36}px;
  padding: 4px 20px;
  ${media.tablet`
    height: ${({ $height }) => $height || 28}px;
    padding: 2px 8px;
  `};
  ${media.mobile`
    height: ${({ $height }) => $height || 24}px;
  `};
`;

const LinkContainer = styled(Link)`
  ${baseContainerStyle}

  background-color: var(--background-color-quaternary, rgba(71, 71, 71, 0.4));
  color: var(--font-color-primary-on-dark, #fff);

  height: ${({ $height, $isInOverview }) =>
    $height || ($isInOverview ? 36 : 24)}px;
  padding: ${({ $isInOverview }) => ($isInOverview ? '4px 20px' : '0 8px')};

  ${media.mobile`
    height: ${({ $height, $isInOverview }) => $height || ($isInOverview ? 28 : 24)}px;
    padding: 0px 8px;
  `};
`;

const SelectableContainer = styled.button`
  ${ResetButtonStyle};
  ${baseContainerStyle}

  background-color: ${({ $selected }) =>
    $selected
      ? 'var(--icon-button-color-active, rgba(0, 210, 190, 0.2))}'
      : 'var(--background-color-quaternary, rgba(71, 71, 71, 0.4))'};
  color: ${({ $selected }) =>
    $selected
      ? 'var(--font-color-brand, #00d2be)'
      : 'var(--font-color-primary-on-dark, #fff)'};

  height: ${({ $height }) => $height || 36}px;
  padding: 4px 20px;

  ${media.mobile`
    height: ${({ $height }) => $height || 28}px;
    padding: 0px 8px;
  `};

  // Based on design team's feedback,
  // only add hover styles to unselected items.
  &:hover {
    ${({ $selected }) => {
      if (!$selected) {
        return css`
          background-color: var(
            --icon-button-color-loading,
            rgba(71, 71, 71, 0.2)
          );
          color: var(--font-color-secondary-on-dark, rgba(221, 221, 221, 0.8));
        `;
      }
      return '';
    }};
  }
`;

const Text = styled.div`
  ${baseTextStyle}

  font-size: ${({ $fontSize }) => $fontSize || 18}px;
  letter-spacing: 0.027px;

  ${media.tablet`
    font-size: ${({ $fontSize }) => $fontSize || 16}px;
    letter-spacing: 0.024px;
  `};
  ${media.mobile`
    font-size: ${({ $fontSize }) => $fontSize || 14}px;
    letter-spacing: 0.014px;
  `};
`;

const LinkText = styled.div`
  ${baseTextStyle}
  font-size: ${({ $isInOverview }) => ($isInOverview ? 18 : 14)}px;
  ${media.mobile`
    font-size: ${({ $isInOverview }) => ($isInOverview ? 16 : 14)}px;
  `};
`;

const SelectableText = styled.div`
  ${baseTextStyle}

  font-size: ${({ $fontSize }) => $fontSize || 18}px;
  letter-spacing: 0.027px;

  ${media.mobile`
    font-size: ${({ $fontSize }) => $fontSize || 16}px;
    letter-spacing: 0.024px;
  `};
`;

export default withTranslation()(CategoryItem);
